import { z } from 'zod';
import { AdPlatform, CampaignObjective } from '@/shared/types/enums';
import { isPastInputValue, toDateInputValue } from '@/shared/utils/date-input';
import { dateFieldSchema } from '../logic/date-field';
import { PLATFORM_MIN_DAILY_BUDGET } from '../types/campaigns.types';

/** Objectives that Meta optimizes against a Pixel + conversion event. */
export const CONVERSION_OBJECTIVES: CampaignObjective[] = [
  CampaignObjective.CONVERSIONS,
  CampaignObjective.TICKET_SALES,
];

/** Standard Meta custom-event types we let organizers pick from. */
export const META_CONVERSION_EVENTS = [
  { value: 'PURCHASE', label: 'Purchase' },
  { value: 'INITIATE_CHECKOUT', label: 'Initiate checkout' },
  { value: 'ADD_TO_CART', label: 'Add to cart' },
  { value: 'LEAD', label: 'Lead' },
  { value: 'COMPLETE_REGISTRATION', label: 'Complete registration' },
] as const;

/** Predicate used by the wizard to show the Pixel / landing URL inputs. */
export function requiresPixel(
  objective: CampaignObjective | undefined,
  platform: AdPlatform | undefined,
): boolean {
  if (platform !== AdPlatform.META) return false;
  if (!objective) return false;
  return CONVERSION_OBJECTIVES.includes(objective);
}

export const objectiveBudgetSchema = z
  .object({
    managementType: z.enum(['SELF_MANAGED', 'EVENTS_MANAGED']),
    platform: z.nativeEnum(AdPlatform, {
      errorMap: () => ({ message: 'Please select an advertising platform' }),
    }),
    adAccountId: z.string().optional(),
    pageId: z.string().optional(),
    pageName: z.string().optional(),
    name: z
      .string()
      .min(3, 'Campaign name must be at least 3 characters')
      .max(100, 'Campaign name must be at most 100 characters'),
    objective: z.nativeEnum(CampaignObjective, {
      errorMap: () => ({ message: 'Please select a campaign objective' }),
    }),
    // Meta rejects launches where both daily AND lifetime budgets are sent,
    // so we force the user to pick exactly one mode upfront.
    budgetStrategy: z.enum(['daily', 'lifetime'], {
      errorMap: () => ({ message: 'Please pick a budget type' }),
    }),
    dailyBudgetCents: z.number().min(0, 'Daily budget must be zero or positive'),
    totalBudgetCents: z.number().min(0, 'Total budget must be zero or positive'),
    currency: z.string().min(1, 'Currency is required'),
    startDate: dateFieldSchema('Start date'),
    endDate: dateFieldSchema('End date'),
    pixelId: z.string().optional(),
    conversionEvent: z.string().optional(),
    landingUrl: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.managementType === 'SELF_MANAGED') {
        return !!data.adAccountId && data.adAccountId.length > 0;
      }
      return true;
    },
    { message: 'Please select an ad account', path: ['adAccountId'] },
  )
  .refine(
    (data) => {
      // Meta self-managed launches need a Facebook Page on the ad account
      // (object_story_spec.page_id) — block the wizard early instead of
      // letting Meta reject mid-launch.
      if (data.managementType === 'SELF_MANAGED' && data.platform === AdPlatform.META) {
        return !!data.pageId && data.pageId.length > 0;
      }
      return true;
    },
    {
      message: 'Pick the Facebook Page that will publish the ad',
      path: ['pageId'],
    },
  )
  .refine(
    (data) => {
      const start = toDateInputValue(data.startDate);
      const end = toDateInputValue(data.endDate);
      // An unparseable date is already reported on the field itself; comparing
      // it here would stack a bogus "end before start" on top of that error.
      if (!start || !end) return true;
      // Both sides are zero-padded YYYY-MM-DD, so lexical order is date order.
      return end > start;
    },
    { message: 'End date must be after start date', path: ['endDate'] },
  )
  // Every ad platform rejects a flight that starts before today. Catch it in
  // the wizard rather than at launch, when the organizer has already filled in
  // targeting and creatives.
  .refine((data) => !isPastInputValue(data.startDate), {
    message: "Start date can't be in the past",
    path: ['startDate'],
  })
  .refine(
    (data) => {
      if (data.budgetStrategy !== 'daily') return true;
      const minDaily = getMinDailyBudget(data.platform);
      return data.dailyBudgetCents >= minDaily;
    },
    {
      message: 'Daily budget is below the minimum for this platform',
      path: ['dailyBudgetCents'],
    },
  )
  .refine(
    (data) => {
      if (data.budgetStrategy !== 'lifetime') return true;
      return data.totalBudgetCents > 0;
    },
    {
      message: 'Lifetime budget is required',
      path: ['totalBudgetCents'],
    },
  )
  .refine(
    (data) =>
      !requiresPixel(data.objective, data.platform) || (!!data.pixelId && data.pixelId.length > 0),
    {
      message: 'Select a Meta Pixel for conversion campaigns',
      path: ['pixelId'],
    },
  )
  .refine(
    (data) => {
      if (!requiresPixel(data.objective, data.platform)) return true;
      if (!data.landingUrl) return false;
      try {
        const url = new URL(data.landingUrl);
        return url.protocol === 'https:';
      } catch {
        return false;
      }
    },
    {
      message: 'Landing URL must be a valid HTTPS URL',
      path: ['landingUrl'],
    },
  );

export type ObjectiveBudgetFormValues = z.infer<typeof objectiveBudgetSchema>;

/** Returns the minimum daily budget in cents for a given platform */
export function getMinDailyBudget(platform: AdPlatform): number {
  return PLATFORM_MIN_DAILY_BUDGET[platform] ?? 100;
}

/** Returns formatted minimum budget string for display (e.g. "$1.00") */
export function formatMinBudget(platform: AdPlatform): string {
  const cents = getMinDailyBudget(platform);
  return `$${(cents / 100).toFixed(2)}`;
}
